import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaInstagram, FaEnvelope, FaFacebook, FaArrowLeft, FaPlus } from 'react-icons/fa';
import { SiTiktok } from 'react-icons/si';
import { auth, db } from '../firebase';
import { doc, collection, onSnapshot, query, where, limit, getDocs } from 'firebase/firestore';
import { useOrganization } from '../context/OrganizationContext';
import LanguageSelector from '../components/LanguageSelector';
import { useTranslation } from 'react-i18next';
import { useChannelsOperational } from '../hooks/useChannelOperational';
import './MultiChannels.css';

const MultiChannels = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('channels');
  const { t: tc } = useTranslation('common');
  const { context } = useOrganization();
  // ig/tiktok operational badge from the helper (tri-state). gmail stays on its singleton
  // (not messaging-capable); whatsapp via checkWhatsAppConnection (su propia vía).
  const operational = useChannelsOperational(['instagram', 'tiktok']);
  const [integrationStatus, setIntegrationStatus] = useState({
    whatsapp: false,
    instagram: false,
    tiktok: false,
    gmail: false
  });
  const [user, setUser] = useState(null);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(setUser);
    return () => unsubscribe();
  }, []);

  // Listen to WhatsApp connection status
  useEffect(() => {
    if (!user) return;

    const checkWhatsAppConnection = async () => {
      try {
        // Filter by organization context
        let q;
        if (context.mode === 'organization') {
          q = query(
            collection(db, `clientes/${user.uid}/numeros`),
            where('organizationId', '==', context.orgId),
            where('estado', '==', 'conectado'),
            limit(1)
          );
        } else {
          q = query(
            collection(db, `clientes/${user.uid}/numeros`),
            where('organizationId', '==', null),
            where('estado', '==', 'conectado'),
            limit(1)
          );
        }

        const snapshot = await getDocs(q);
        setIntegrationStatus(prev => ({
          ...prev,
          whatsapp: !snapshot.empty
        }));
      } catch (error) {
        console.error('Error checking WhatsApp connection:', error);
      }
    };

    checkWhatsAppConnection();
  }, [user, context]);

  // Listen to integration status for gmail (singleton). ig/tiktok now come from the
  // operational helper (channelAccounts); whatsapp from checkWhatsAppConnection.
  useEffect(() => {
    if (!user) return;

    const unsubscribers = [];

    const basePath = context.mode === 'organization' && context.orgId
      ? `organizations/${context.orgId}`
      : `clientes/${user.uid}`;

    ['gmail'].forEach(provider => {
      const integrationRef = doc(db, basePath, 'integrations', provider);
      const unsubscribe = onSnapshot(integrationRef, (doc) => {
        setIntegrationStatus(prev => ({
          ...prev,
          [provider]: doc.exists() && doc.data().connected === true
        }));
      });
      unsubscribers.push(unsubscribe);
    });

    return () => unsubscribers.forEach(unsub => unsub());
  }, [user, context]);

  const getChannelStatus = (channelId) => {
    switch (channelId) {
      case 'instagram':
        return operational.instagram === undefined ? 'checking' : operational.instagram ? 'connected' : 'disconnected';
      case 'tiktok':
        return operational.tiktok === undefined ? 'checking' : operational.tiktok ? 'connected' : 'disconnected';
      case 'email':
        return integrationStatus.gmail ? 'connected' : 'disconnected';
      case 'facebook':
        return 'coming-soon';
      default:
        return 'disconnected';
    }
  };

  const getChannelDescription = (channelId, status) => {
    if (status === 'connected') {
      return 'Conectado y activo';
    } else if (status === 'checking') {
      return t('social.checkingStatus', 'Verificando estado…');
    } else if (status === 'coming-soon') {
      return 'Próximamente';
    } else {
      return 'Desconectado';
    }
  };

  const channels = [
    {
      id: 'instagram',
      name: 'Instagram',
      icon: <FaInstagram />,
      action: () => navigate('/integrations')
    },
    {
      id: 'tiktok',
      name: 'TikTok',
      icon: <SiTiktok />,
      action: () => navigate('/integrations')
    },
    {
      id: 'email',
      name: 'Gmail',
      icon: <FaEnvelope />,
      action: () => navigate('/integrations')
    },
    {
      id: 'facebook',
      name: 'Facebook Messenger',
      icon: <FaFacebook />,
      action: null
    }
  ].map(channel => ({
    ...channel,
    status: getChannelStatus(channel.id),
    description: getChannelDescription(channel.id, getChannelStatus(channel.id))
  }));

  const getStatusColor = (status) => {
    switch (status) {
      case 'connected': return '#4caf50';
      case 'disconnected': return '#ff9800';
      case 'checking': return '#9e9e9e';
      case 'coming-soon': return '#9e9e9e';
      default: return '#9e9e9e';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'connected': return t('whatsappWeb.connected').replace('✅ ', '');
      case 'disconnected': return t('whatsappWeb.disconnected').replace('❌ ', '');
      case 'checking': return t('social.checkingStatus', 'Verificando estado…');
      case 'coming-soon': return tc('processing').replace('...', '');
      default: return tc('error');
    }
  };

  return (
    <div className="multi-channels-container">
      <div className="multi-channels-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button onClick={() => navigate('/dashboard')} className="back-button">
            <FaArrowLeft /> {tc('backToDashboard').replace('← ', '')}
          </button>
          <LanguageSelector />
        </div>
        <h1>{t('title')}</h1>
        <p>{t('subtitle')}</p>
      </div>

      <div className="channels-grid">
        {channels.map(channel => (
          <div 
            key={channel.id} 
            className={`channel-card ${channel.status}`}
            onClick={channel.action}
            style={{ cursor: channel.action ? 'pointer' : 'not-allowed' }}
          >
            <div className="channel-icon" style={{ color: getStatusColor(channel.status) }}>
              {channel.icon}
            </div>
            <h3>{channel.name}</h3>
            <p className="channel-description">{channel.description}</p>
            <div className="channel-status">
              <span 
                className="status-badge" 
                style={{ backgroundColor: getStatusColor(channel.status) }}
              >
                {getStatusText(channel.status)}
              </span>
            </div>
            {channel.action && channel.status !== 'coming-soon' && (
              <button className="channel-action-btn">
                {channel.status === 'connected' ? 'Gestionar' : 'Conectar'}
              </button>
            )}
          </div>
        ))}
      </div>

      <div className="multi-channels-footer">
        <div className="stats-container">
          <div className="stat">
            <span className="stat-value">
              {channels.filter(c => c.status === 'connected').length}
            </span>
            <span className="stat-label">Canales activos</span>
          </div>
          <div className="stat">
            <span className="stat-value">
              {channels.filter(c => c.status === 'disconnected').length}
            </span>
            <span className="stat-label">Disponibles</span>
          </div>
          <div className="stat">
            <span className="stat-value">
              {channels.filter(c => c.status === 'coming-soon').length}
            </span>
            <span className="stat-label">Próximamente</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MultiChannels;